// TODO: To create a separate hook files for the FavouritePopin
import Button from '@material-ui/core/Button';
import AddIcon from '@material-ui/icons/Add';
import DoneIcon from '@material-ui/icons/Done';
import RemoveIcon from '@material-ui/icons/Remove';
import SearchIcon from '@material-ui/icons/Search';
import DrillxInput from 'components/DrillxInput';
import DrillxPopin from 'components/DrillxPopin';
import { ObjectKeys } from 'helpers/ts-helpers';
import { CategorizedWells, useFetchAllCategorizedWells } from 'hooks/useFetchAllCategorizedWells';
import React, {
  ChangeEvent,
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { WellFullDetailsWithUniqueKeyDTO } from 'types/well';

import styles from './FavouritePopin.module.scss';
import FavouriteWellSwitch from './components/FavouriteWellSwitch';
import AdvanceSearch from '../AdvanceSearch';
import { wellNameType } from '../AdvanceSearch/AdvanceSearch';

interface Props {
  open: boolean;
  onClose: () => void;
}

const MINIMUM_NUMBER_OF_CHARACTERS_IN_SEARCH = 2;
const SELECT_ALL = 'Select All';
const UNSELECT_ALL = 'Unselect All';

const FavouritePopin: React.FC<Props> = ({ open, onClose }) => {
  const intl = useIntl();
  const [selectedCountries, setSelectedContries] = useState<string[]>([]);
  const [debouncedSearchInput, setSearchInput] = useState('');
  const [selectedCategorisedWells, setSelectedCategorisedWells] = useState<CategorizedWells>(
    {} as CategorizedWells,
  );
  const [endPage, setEndPage] = useState(20);
  const [isShowAdvancedSearch, setAdvancedSearch] = useState(false);
  const [isShowReportingWells, setShowReportingWells] = useState(false);
  const [isShowLiveWells, setShowLiveWells] = useState(false);
  const [isPopinOpen, setPopinOpen] = useState(false);
  const [contriesList, setCountrieList] = useState<(string | undefined)[]>([]);
  const [isSelectedAllCheckBox, setSelectedAllCheckBox] = useState(false);

  const { categorizedWells } = useFetchAllCategorizedWells(true);

  const filterWells = useCallback(
    (wells: WellFullDetailsWithUniqueKeyDTO[], category: keyof CategorizedWells) => {
      if (
        category === 'nonActiveWells' &&
        (debouncedSearchInput?.length ?? 0) < MINIMUM_NUMBER_OF_CHARACTERS_IN_SEARCH
      ) {
        return [];
      }

      return debouncedSearchInput
        ? wells.filter(well =>
            well.unifiedWellName.toLowerCase().includes(debouncedSearchInput.toLowerCase()),
          )
        : wells;
    },
    [debouncedSearchInput],
  );

  const onSearchInputChanged = ({ currentTarget: { value } }: ChangeEvent<HTMLInputElement>) =>
    setSearchInput(value);

  const getAllCountries = useMemo(
    () => [...new Set(categorizedWells.wellsByCountry.map(well => well.country))],
    [categorizedWells],
  );
  getAllCountries.sort();

  useEffect(() => {
    if (open) setPopinOpen(true);
  }, [open]);

  const onscroll = (e: React.UIEvent<HTMLElement>) => {
    if (isPopinOpen) {
      const bottom =
        e.currentTarget.offsetHeight + e.currentTarget.scrollTop >= e.currentTarget.scrollHeight;

      if (bottom && endPage !== selectedCategorisedWells.wellsByCountry.length - 1) {
        setEndPage(endPage + 200);
      }
    }
  };

  const onSelectCountries = (event: React.ChangeEvent<wellNameType>) => {
    const value = event.target.value as string[];
    if (value.filter(data => data === SELECT_ALL || data === UNSELECT_ALL).length > 0) {
      if (!isSelectedAllCheckBox) {
        setSelectedContries(getAllCountries as string[]);
      } else {
        setSelectedContries([] as string[]);
      }
      setSelectedAllCheckBox(!isSelectedAllCheckBox);
    } else {
      setSelectedContries(event.target.value as string[]);
    }
  };

  const onChangeLiveToggle = () => {
    setShowLiveWells(!isShowLiveWells);
  };

  const onChangeReportingToggle = () => {
    setShowReportingWells(!isShowReportingWells);
  };

  const resetCategorisedWellsToDefault = () => {
    setSelectedCategorisedWells(categorizedWells);
  };

  const resetContriesListToDefault = () => {
    setCountrieList(getAllCountries);
  };

  const onClickClearIcon = () => {
    setEndPage(20);
    resetCategorisedWellsToDefault();
    resetContriesListToDefault();
    setSelectedContries([]);
    setSelectedAllCheckBox(false);
  };

  const resetAdvancedSearchComponentsToDefault = () => {
    setSelectedContries([]);
    setShowLiveWells(false);
    setShowReportingWells(false);
    setSelectedAllCheckBox(false);
  };

  const onClickAdvancedSearch = () => {
    setAdvancedSearch(!isShowAdvancedSearch);
    setSearchInput('');
    setEndPage(20);
    setSearchInput('');
    if (isShowAdvancedSearch) {
      resetCategorisedWellsToDefault();
      resetContriesListToDefault();
    }
    resetAdvancedSearchComponentsToDefault();
  };

  const onPopinClose = useCallback(() => {
    setPopinOpen(false);
    setEndPage(20);
    setSearchInput('');
    onClose();
    setAdvancedSearch(false);
    setSelectedCategorisedWells(categorizedWells);
    setCountrieList(getAllCountries);
    resetAdvancedSearchComponentsToDefault();
  }, [onClose, categorizedWells, getAllCountries]);

  const getCountiesSelectElements = () => {
    if (getAllCountries !== undefined && getAllCountries?.length > 0) {
      const countriesList = [...getAllCountries];
      // remove the undefined object from the array
      countriesList.pop();
      if (isSelectedAllCheckBox) {
        countriesList.unshift(UNSELECT_ALL);
      } else {
        countriesList.unshift(SELECT_ALL);
      }

      return countriesList;
    }
  };

  /* This useEffect is used to set the countriesList on initial load */
  useEffect(() => {
    if (getAllCountries.length > 0) {
      setCountrieList(getAllCountries);
    }
  }, [getAllCountries, getAllCountries.length]);

  /* This is useEffect is used to set the categorisedWells on initial load */
  useEffect(() => {
    if (categorizedWells !== undefined) {
      setSelectedCategorisedWells(categorizedWells);
    }
  }, [categorizedWells]);

  /* This useEffect is used to set the categorisedWells based on advancedSearch inputs */
  useEffect(() => {
    if (isShowAdvancedSearch) {
      if (selectedCountries.length === 0 && !isShowLiveWells && !isShowReportingWells) {
        setSelectedCategorisedWells(categorizedWells);
        setCountrieList(getAllCountries);
      } else if (selectedCountries.length > 0) {
        const filteredWellByCountries: WellFullDetailsWithUniqueKeyDTO[] = [];
        const filteredActiveSensorWells: WellFullDetailsWithUniqueKeyDTO[] = [];
        const filteredActiveWellviewWells: WellFullDetailsWithUniqueKeyDTO[] = [];
        const countries: string[] = [];
        const formattedCategorisedWells = { ...categorizedWells };
        selectedCountries.map(country => {
          countries.push(country);

          return formattedCategorisedWells.wellsByCountry.filter(well => {
            if (well.country === country) {
              filteredWellByCountries.push(well);
            }

            return filteredWellByCountries;
          });
        });
        if (isShowLiveWells) {
          selectedCountries.map(country => {
            return formattedCategorisedWells.activeSensorWells.filter(well => {
              if (well.country === country) {
                filteredActiveSensorWells.push(well);
              }

              return filteredActiveSensorWells;
            });
          });
        }
        if (isShowReportingWells) {
          selectedCountries.map(country => {
            return formattedCategorisedWells.activeWellviewWells.filter(well => {
              if (well.country === country) {
                filteredActiveWellviewWells.push(well);
              }

              return filteredActiveWellviewWells;
            });
          });
        }
        formattedCategorisedWells.activeSensorWells = filteredActiveSensorWells;
        formattedCategorisedWells.activeWellviewWells = filteredActiveWellviewWells;
        formattedCategorisedWells.favouriteWells = [];
        formattedCategorisedWells.nonActiveWells = [];
        formattedCategorisedWells.wellsByCountry = filteredWellByCountries;
        setSelectedCategorisedWells(formattedCategorisedWells);
        setCountrieList(countries);
      } else if (
        isShowLiveWells ||
        isShowReportingWells ||
        !isShowLiveWells ||
        !isShowReportingWells
      ) {
        const formattedCategorisedWells = { ...categorizedWells };
        const formattedActiveSensorWells = formattedCategorisedWells.activeSensorWells;
        const formattedActiveWellviewWells = formattedCategorisedWells.activeWellviewWells;
        if (isShowLiveWells) {
          formattedCategorisedWells.activeSensorWells = formattedActiveSensorWells;
        } else {
          formattedCategorisedWells.activeSensorWells = [];
        }

        if (isShowReportingWells) {
          formattedCategorisedWells.activeWellviewWells = formattedActiveWellviewWells;
        } else {
          formattedCategorisedWells.activeWellviewWells = [];
        }
        formattedCategorisedWells.favouriteWells = [];
        formattedCategorisedWells.nonActiveWells = [];
        formattedCategorisedWells.wellsByCountry = [];
        setSelectedCategorisedWells(formattedCategorisedWells);
        setCountrieList([]);
      }
    }
  }, [
    selectedCountries,
    isShowLiveWells,
    isShowReportingWells,
    categorizedWells,
    getAllCountries,
    isShowAdvancedSearch,
  ]);

  return (
    <DrillxPopin
      {...{ open, onClose: onPopinClose, onscroll }}
      className={styles.popin}
      disableBackdropClick={true}
      ariaLabelledby={intl.formatMessage({ id: 'favouriteWellsPopin.ariaLabelledby' })}
      actions={
        <Button
          variant="contained"
          className={styles.doneButton}
          startIcon={<DoneIcon />}
          onClick={onPopinClose}
        >
          {intl.formatMessage({ id: 'favouriteWellsPopin.done' })}
        </Button>
      }
    >
      <div className={styles.textContainer}>
        <h1>{intl.formatMessage({ id: 'favouriteWellsPopin.welcomeText' })}</h1>
        <p>
          {intl.formatMessage(
            {
              id: 'favouriteWellsPopin.description',
            },
            { minimumNumberOfCharactersInSearch: MINIMUM_NUMBER_OF_CHARACTERS_IN_SEARCH },
          )}
        </p>
      </div>
      <div>
        <DrillxInput
          placeholder={intl.formatMessage({ id: 'favouriteWellsPopin.search' })}
          className={styles.searchBar}
          onChange={onSearchInputChanged}
          autoFocus
          value={debouncedSearchInput}
          endAdornment={<SearchIcon className={styles.searchIcon} />}
        />
      </div>
      <div>
        <Button
          classes={{
            root: styles.advancedSearchButton,
            startIcon: styles.advancedSearchIcon,
          }}
          color="inherit"
          size="small"
          startIcon={isShowAdvancedSearch ? <RemoveIcon /> : <AddIcon />}
          onClick={() => onClickAdvancedSearch()}
        >
          <span className={styles.advancedSearchText}>Advance Search</span>
        </Button>
      </div>
      {isShowAdvancedSearch && (
        <AdvanceSearch
          onSelectCountries={event => onSelectCountries(event)}
          onChangeLiveToggle={() => onChangeLiveToggle()}
          onChangeReportingToggle={() => onChangeReportingToggle()}
          onClickClearIcon={() => onClickClearIcon()}
          countries={getCountiesSelectElements()}
          selectedCountries={selectedCountries}
          isLiveDataSwitchActive={isShowLiveWells}
          isReportingDataSwitchActive={isShowReportingWells}
          isSelectedAllCheckBox={isSelectedAllCheckBox}
        />
      )}

      <div onScroll={onscroll} className={styles.wellsContainer}>
        {ObjectKeys(selectedCategorisedWells).map(category =>
          category !== 'wellsByCountry' ? (
            <Fragment key={category}>
              <div className={styles.wellCategoryLabelContainer}>
                {intl.formatMessage({ id: `well-select.categories.${category}` })}
              </div>
              {filterWells(selectedCategorisedWells[category], category).map(well => (
                <div key={well.key} className={styles.wellSwitchContainer}>
                  <FavouriteWellSwitch well={well} />
                </div>
              ))}
            </Fragment>
          ) : (
            <Fragment key={category}>
              <div className={styles.wellCategoryLabelContainer}>
                {intl.formatMessage({ id: `well-select.categories.${category}` })}
              </div>
              {contriesList.map((country, index) => (
                <div key={+index}>
                  <div className={styles.wellSubCategoryLabelContainer}>{country}</div>
                  {filterWells(selectedCategorisedWells[category], category).map((well, index) =>
                    well.country === country && index < endPage ? (
                      <div key={well.key} className={styles.wellSwitchContainer}>
                        <FavouriteWellSwitch well={well} />
                      </div>
                    ) : null,
                  )}
                </div>
              ))}
            </Fragment>
          ),
        )}
      </div>
    </DrillxPopin>
  );
};

export default memo(FavouritePopin);
